import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { ArrowRight, CalendarClock, Coins, Eye, Heart, ImageIcon, Mail, MailOpen, Send } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/skeleton';

import { PeriodControl } from '../../../components/design-system/analytics/PeriodControl';
import { PageContainer } from '../../../common/components/PageContainer';
import { Compared, MarketingOverview, useHomeApi } from '../../../common/hooks/use-home-api.hook';
import { StatCard } from '../../../modules/vendor/dashboard/components/stat-card.component';
import { BarList, useInsightsRange } from '../assistant/assistant-shared';

/**
 * The Marketing workspace's dashboard.
 *
 * What went out and what it did, across the three ways a vendor markets
 * through us: social posts, email, and the images Content Studio made for
 * them. Each tile links to where the work is done; this page only reads.
 */
const MarketingOverviewBody = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const api = useHomeApi();
    const { range, periodProps } = useInsightsRange();
    const [data, setData] = useState<MarketingOverview | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.getMarketing(range)
            .then((r) => { if (!cancelled) setData(r); })
            .catch((error) => { console.error('Error loading marketing overview:', error); if (!cancelled) setData(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range.startDate, range.endDate]);

    // Only rises that are good news get the arrow: more reach, more opens, more
    // images. Credits spent is a cost, so its change is stated, not coloured.
    const change = (figure?: Compared | null) => (figure?.change ? Math.round(figure.change) : 0);
    const stat = (title: string, value: string | number, icon: React.ReactNode, hint = '', trend = 0) => (
        <StatCard title={title} value={value} icon={icon} loading={loading} percentageChange={trend} comparisonLabel={hint} />
    );

    const social = data?.social;
    const email = data?.email;
    const studio = data?.studio;
    const chartConfig: ChartConfig = { sent: { label: t('MARKETING_OVERVIEW.EMAILS_SENT', 'Emails sent'), color: 'var(--foreground)' } };
    const none = t('MARKETING_OVERVIEW.NO_DATA', 'Nothing in this period yet.');

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl font-medium tracking-tight">{t('MARKETING_OVERVIEW.TITLE', 'Marketing')}</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {t('MARKETING_OVERVIEW.SUBTITLE', 'What went out — posts, emails and images — and what it did.')}
                    </p>
                </div>
                <PeriodControl {...periodProps} />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {stat(t('MARKETING_OVERVIEW.POSTS_PUBLISHED', 'Posts published'), social?.totalPublished ?? 0, <Send />,
                    t('MARKETING_OVERVIEW.POSTS_HINT', '{{count}} scheduled', { count: social?.statusBreakdown?.scheduled ?? 0 }))}
                {stat(t('MARKETING_OVERVIEW.REACH', 'Reach'), (social?.totals.reach ?? 0).toLocaleString(), <Eye />,
                    t('MARKETING_OVERVIEW.IMPRESSIONS_HINT', '{{count}} impressions', { count: social?.totals.impressions ?? 0 }))}
                {stat(t('MARKETING_OVERVIEW.ENGAGEMENT', 'Engagement rate'), social ? `${social.avgEngagementRate}%` : '—', <Heart />,
                    t('MARKETING_OVERVIEW.ENGAGEMENT_HINT', '{{likes}} likes · {{comments}} comments', { likes: social?.totals.likes ?? 0, comments: social?.totals.comments ?? 0 }))}
                {stat(t('MARKETING_OVERVIEW.EMAILS_SENT', 'Emails sent'), (email?.sent.value ?? 0).toLocaleString(), <Mail />, '', change(email?.sent))}
                {stat(t('MARKETING_OVERVIEW.OPEN_RATE', 'Email open rate'), email?.openRate.value !== null && email?.openRate.value !== undefined ? `${email.openRate.value}%` : '—', <MailOpen />,
                    t('MARKETING_OVERVIEW.CLICK_HINT', '{{rate}}% clicked', { rate: email?.clickRate.value ?? 0 }), change(email?.openRate))}
                {stat(t('MARKETING_OVERVIEW.IMAGES', 'Images made'), (studio?.images.value ?? 0).toLocaleString(), <ImageIcon />, '', change(studio?.images))}
                {stat(t('MARKETING_OVERVIEW.CREDITS', 'Credits spent'), (studio?.creditsSpent.value ?? 0).toLocaleString(), <Coins />,
                    studio?.creditsSpent.previous !== null && studio?.creditsSpent.previous !== undefined
                        ? t('MARKETING_OVERVIEW.PREVIOUS', 'Previous period: {{value}}', { value: studio.creditsSpent.previous.toLocaleString() })
                        : '')}
                {stat(t('MARKETING_OVERVIEW.UPCOMING', 'Posts in the next 14 days'), data?.upcoming.length ?? 0, <CalendarClock />)}
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
                <Card className="xl:col-span-2">
                    <CardHeader>
                        <CardTitle>{t('MARKETING_OVERVIEW.RECENT_IMAGES', 'Recent images')}</CardTitle>
                        <CardDescription>{t('MARKETING_OVERVIEW.RECENT_IMAGES_DESC', 'Made in Content Studio, ready to post or advertise.')}</CardDescription>
                        <CardAction>
                            <Button variant="ghost" size="sm" onClick={() => navigate('/marketing/content-studio')}>
                                {t('MARKETING_OVERVIEW.OPEN_STUDIO', 'Open Content Studio')}
                                <ArrowRight data-icon="inline-end" />
                            </Button>
                        </CardAction>
                    </CardHeader>
                    <CardContent>
                        {loading ? <Skeleton className="h-40 w-full" /> : data?.recentImages.length ? (
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                {data.recentImages.map((image) => (
                                    <button
                                        key={image.uuid}
                                        type="button"
                                        onClick={() => navigate('/marketing/content-studio')}
                                        className="aspect-square overflow-hidden rounded-lg border bg-muted"
                                    >
                                        <img src={image.url} alt="" loading="lazy" className="size-full object-cover transition-transform duration-500 hover:scale-105" />
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <p className="py-8 text-center text-sm text-muted-foreground">{none}</p>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('MARKETING_OVERVIEW.COMING_UP', 'Coming up')}</CardTitle>
                        <CardDescription>{t('MARKETING_OVERVIEW.COMING_UP_DESC', 'Posts scheduled for the next two weeks.')}</CardDescription>
                        <CardAction>
                            <Button variant="ghost" size="sm" onClick={() => navigate('/marketing/social-scheduler')}>
                                {t('MARKETING_OVERVIEW.OPEN_PLANNER', 'Planner')}
                                <ArrowRight data-icon="inline-end" />
                            </Button>
                        </CardAction>
                    </CardHeader>
                    <CardContent>
                        {loading ? <Skeleton className="h-40 w-full" /> : data?.upcoming.length ? (
                            <ul className="flex flex-col divide-y">
                                {data.upcoming.map((post) => (
                                    <li key={post.uuid} className="flex items-center gap-3 py-2.5">
                                        <span className="size-12 shrink-0 overflow-hidden rounded-md border bg-muted">
                                            {post.imageUrl && <img src={post.imageUrl} alt="" loading="lazy" className="size-full object-cover" />}
                                        </span>
                                        <span className="flex min-w-0 flex-col gap-0.5">
                                            <span className="text-xs text-muted-foreground">
                                                {dayjs(post.scheduledAt).format('ddd D MMM, HH:mm')} · {post.platform === 'meta' ? 'Facebook' : post.platform.charAt(0).toUpperCase() + post.platform.slice(1)}
                                            </span>
                                            <span className="line-clamp-2 text-sm">{post.caption || '—'}</span>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="flex flex-col items-center gap-3 py-8 text-center text-sm text-muted-foreground">
                                {t('MARKETING_OVERVIEW.NOTHING_SCHEDULED', 'Nothing scheduled.')}
                                <Button variant="outline" size="sm" onClick={() => navigate('/marketing/social-scheduler')}>
                                    {t('MARKETING_OVERVIEW.PLAN_POST', 'Plan a post')}
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
                <Card className="xl:col-span-2">
                    <CardHeader>
                        <CardTitle>{t('MARKETING_OVERVIEW.EMAILS_SENT', 'Emails sent')}</CardTitle>
                        <CardAction>
                            <Button variant="ghost" size="sm" onClick={() => navigate('/marketing/email/automation')}>
                                {t('MARKETING_OVERVIEW.OPEN_EMAIL', 'Email')}
                                <ArrowRight data-icon="inline-end" />
                            </Button>
                        </CardAction>
                    </CardHeader>
                    <CardContent>
                        {loading ? <Skeleton className="h-56 w-full" /> : email?.series.length ? (
                            <ChartContainer config={chartConfig} className="h-56 w-full">
                                <AreaChart data={email.series} margin={{ left: 0, right: 8, top: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="date" tickLine={false} axisLine={false} minTickGap={24} tickFormatter={(d: string) => d.slice(5)} />
                                    <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={32} />
                                    <ChartTooltip content={<ChartTooltipContent />} />
                                    <Area dataKey="sent" type="monotone" stroke="var(--color-sent)" strokeWidth={2} fill="var(--color-sent)" fillOpacity={0.08} />
                                </AreaChart>
                            </ChartContainer>
                        ) : (
                            <p className="py-10 text-center text-sm text-muted-foreground">{none}</p>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('MARKETING_OVERVIEW.BY_PLATFORM', 'Published by platform')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? <Skeleton className="h-24 w-full" /> : (
                            <BarList
                                empty={none}
                                items={Object.entries(social?.platformBreakdown ?? {})
                                    .map(([platform, value]) => ({
                                        label: platform === 'meta' ? 'Facebook' : platform.charAt(0).toUpperCase() + platform.slice(1),
                                        value,
                                    }))
                                    .sort((a, b) => b.value - a.value)}
                            />
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export const MarketingOverviewPage = () => {
    const { t } = useTranslation();
    return (
        <PageContainer breadcrumbs={[{ label: t('SIDEBAR.MARKETING.TITLE') }, { label: t('SIDEBAR.DASHBOARD.TITLE') }]}>
            <MarketingOverviewBody />
        </PageContainer>
    );
};

export default MarketingOverviewPage;
